import type { ChatPrompt, ChatPromptScope } from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAppBootstrap } from "@/lib/hooks/use-app-bootstrap";
import {
  useChatPrompts,
  useCreateChatPrompt,
  useDeleteChatPrompt,
  useUpdateChatPrompt,
} from "@/lib/hooks/use-chat-prompts";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./prompts.i18n";

export function Prompts() {
  const t = useT(T);
  const { data: bootstrap } = useAppBootstrap();
  const isDesigner = bootstrap?.user.permissions?.includes("designer") ?? false;

  const visibleScopes: ChatPromptScope[] = isDesigner
    ? ["global", "client", "system"]
    : ["global", "client"];

  const [scope, setScope] = useState<ChatPromptScope>("global");

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">{t.scope}</span>
        {visibleScopes.map((s) => (
          <button
            key={s}
            type="button"
            className={`px-2 py-1 rounded-md transition-colors ${
              scope === s
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted"
            }`}
            onClick={() => setScope(s)}
          >
            {t.scopes[s]}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{t.scopeDescriptions[scope]}</p>
      <PromptList scope={scope} />
    </div>
  );
}

function PromptList({ scope }: { scope: ChatPromptScope }) {
  const t = useT(T);
  const { prompts, isLoading } = useChatPrompts(scope);
  const create = useCreateChatPrompt();
  const [adding, setAdding] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [textDraft, setTextDraft] = useState("");

  const resetDraft = () => {
    setAdding(false);
    setTitleDraft("");
    setTextDraft("");
  };

  const handleAdd = async () => {
    const title = titleDraft.trim();
    const text = textDraft.trim();
    if (!title || !text) return;
    try {
      await create.mutateAsync({ scope, title, text });
      resetDraft();
    } catch (err) {
      toast.error(t.failedToSave, {
        description: err instanceof Error ? err.message : t.unexpectedError,
      });
    }
  };

  if (isLoading) {
    return <div className="text-xs text-muted-foreground">{t.loading}</div>;
  }

  return (
    <div className="space-y-2">
      {prompts.map((prompt) => (
        <PromptRow key={prompt.id} prompt={prompt} />
      ))}

      {prompts.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground italic">{t.noPrompts}</p>
      )}

      {adding ? (
        <div className="space-y-2 p-2 rounded-md border">
          <Input
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            placeholder={t.titlePlaceholder}
            className="text-xs"
            autoFocus
          />
          <Textarea
            value={textDraft}
            onChange={(e) => setTextDraft(e.target.value)}
            placeholder={t.textPlaceholder}
            className="text-xs min-h-[60vh] resize-none"
          />
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={resetDraft}>
              {t.cancel}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleAdd}
              disabled={!titleDraft.trim() || !textDraft.trim()}
              loading={create.isPending}
            >
              {t.addPrompt}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full gap-1.5"
          onClick={() => setAdding(true)}
        >
          {t.addPrompt}
        </Button>
      )}
    </div>
  );
}

function PromptRow({ prompt }: { prompt: ChatPrompt }) {
  const t = useT(T);
  const update = useUpdateChatPrompt();
  const remove = useDeleteChatPrompt(prompt.scope);
  const [editing, setEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState(prompt.title);
  const [textDraft, setTextDraft] = useState(prompt.text);

  const startEdit = () => {
    setTitleDraft(prompt.title);
    setTextDraft(prompt.text);
    setEditing(true);
  };

  const save = async () => {
    const title = titleDraft.trim();
    const text = textDraft.trim();
    if (!title || !text) return;
    if (title === prompt.title && text === prompt.text) {
      setEditing(false);
      return;
    }
    try {
      await update.mutateAsync({ id: prompt.id, title, text });
      setEditing(false);
    } catch (err) {
      toast.error(t.failedToUpdate, {
        description: err instanceof Error ? err.message : t.unexpectedError,
      });
    }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(prompt.id);
    } catch (err) {
      toast.error(t.failedToDelete, {
        description: err instanceof Error ? err.message : t.unexpectedError,
      });
    }
  };

  if (editing) {
    return (
      <div className="space-y-2 p-2 rounded-md border">
        <Input
          value={titleDraft}
          onChange={(e) => setTitleDraft(e.target.value)}
          placeholder={t.title}
          className="text-xs"
          autoFocus
        />
        <Textarea
          value={textDraft}
          onChange={(e) => setTextDraft(e.target.value)}
          placeholder={t.prompt}
          className="text-xs min-h-[60vh] resize-none"
        />
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
            {t.cancel}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={save}
            disabled={!titleDraft.trim() || !textDraft.trim()}
            loading={update.isPending}
          >
            {t.save}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex items-start gap-2 p-2 rounded-md bg-muted/30 hover:bg-muted/50 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium truncate">{prompt.title}</div>
        <div className="text-xs text-muted-foreground line-clamp-2">{prompt.text}</div>
      </div>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <button
          type="button"
          className="p-1 rounded hover:bg-background text-muted-foreground hover:text-foreground"
          onClick={startEdit}
          aria-label={t.edit}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className="p-1 rounded hover:bg-background text-muted-foreground hover:text-destructive"
          onClick={handleDelete}
          disabled={remove.isPending}
          aria-label={t.delete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
